import { JobContextWarnings } from '@/components/project/JobContextWarnings'
import { AssetProvenanceNotice } from '@/components/multimodal/AssetProvenanceNotice'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, ArrowRight, Clipboard, Cog, Download, Factory, FileText, Inbox, Loader2, Play, Square } from 'lucide-react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { chapterApi } from '@/api/chapterApi'
import { API_BASE, friendlyMessage } from '@/api/http'
import { parseScreenplayPayload, parseStoryboardPanels, type ScreenplayScene } from '@/api/multimodalApi'
import { pulseApi } from '@/api/pulseApi'
import { projectApi } from '@/api/projectApi'
import { SubmitJobDrawer } from '@/components/SubmitJobDrawer'
import { ScreenplayView, StoryboardView, StructuredAssetPagination } from '@/components/asset/StructuredAssetViews'
import { ProjectCombobox } from '@/components/project/ProjectCombobox'
import { ALL_PROJECTS_VALUE } from '@/lib/projectSelection'
import { downloadText } from '@/lib/download'
import { pageItems } from '@/lib/pagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { isRunningStatus, statusLabel } from '@/lib/statusLabel'
import { useAuthStore } from '@/stores/authStore'
import { useJobStore } from '@/stores/jobStore'
import { useProjectStore } from '@/stores/projectStore'
import { useUiStore } from '@/stores/uiStore'
import type { Asset, AssetStatus, AssetType, CascadeState, Chapter, ChapterImage, JobStatus, JobView } from '@/types/nf'
import type { PulseSummaryProject, PulseSummaryStationKey } from '@/types/pulse'
import { formatCount, formatDate, formatDateTime, formatDuration, formatNumber, formatPercent } from '@/utils/format'

const runningStatuses = [
  'pending',
  'structure_planning',
  'paragraph_generating',
  'qc_check',
  'copy_editing',
  'scene_editing',
  'retry_scheduled',
] as const satisfies readonly JobStatus[]

export type RunningJobStatus = (typeof runningStatuses)[number]
type StationAssetType = Extract<AssetType, 'screenplay' | 'storyboard' | 'panel_image' | 'audio' | 'video'>
type DrawerSelection = { kind: 'job'; job: JobView } | { kind: 'asset'; asset: Asset } | null
type ChapterMap = Map<string, Chapter>
type BoardViewMode = 'single' | 'overview'

// 看板视图: 记住上次选择(localStorage); 首次进入(无记录)默认工厂总览
// (deepseek 仲裁: 生产看板第一眼=全局安灯板; 兼顾 opencode/qwen 保守=记住上次)。
const BOARD_VIEW_LS_KEY = 'nf.board-view'
function initialBoardView(): BoardViewMode {
  try {
    const saved = localStorage.getItem(BOARD_VIEW_LS_KEY)
    if (saved === 'single' || saved === 'overview') return saved
  } catch {
    // localStorage 不可用 → 走默认
  }
  return 'overview'
}

const stationAssetTypes = ['screenplay', 'storyboard', 'panel_image', 'audio', 'video'] as const satisfies readonly StationAssetType[]

const boardColumns = [
  { key: 'chapter_text', label: 'station.text' },
  { key: 'screenplay', label: 'station.screenplay' },
  { key: 'storyboard', label: 'station.storyboard' },
  { key: 'panel_image', label: 'station.image' },
  { key: 'audio', label: 'station.audio' },
  { key: 'video', label: 'station.video' },
] as const

// 生产流水线分段: 文字(源头) → 改编(剧本/分镜) → 成片(出图/声音/视频)。
// 6 工位是顺序装配线而非平行队列, 按阶段分组替代扁平并列, 编码真实生产流程。
const boardStages = [
  { marker: '①', labelKey: 'board.stageSource', hintKey: 'board.stageSourceHint', columns: ['chapter_text'], grid: 'grid-cols-1' },
  { marker: '②', labelKey: 'board.stageAdapt', hintKey: 'board.stageAdaptHint', columns: ['screenplay', 'storyboard'], grid: 'grid-cols-1 sm:grid-cols-2' },
  { marker: '③', labelKey: 'board.stageProduce', hintKey: 'board.stageProduceHint', columns: ['panel_image', 'audio', 'video'], grid: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' },
] as const

const PAGE_SIZE = 12

const summaryStationColumns = [
  { key: 'text', label: 'station.text' },
  { key: 'screenplay', label: 'station.screenplay' },
  { key: 'storyboard', label: 'station.storyboard' },
  { key: 'panel_image', label: 'station.image' },
  { key: 'audio', label: 'station.audio' },
  { key: 'video', label: 'station.video' },
] as const satisfies readonly { key: PulseSummaryStationKey; label: string }[]

const payloadSummaryKeys = [
  'quality',
  'score',
  'title',
  'summary',
  'url',
  'image_url',
  'audio_url',
  'video_url',
  'duration',
  'duration_ms',
  'word_count',
  'panel_count',
  'scene_count',
] as const

export function isRunningJobStatus(status: JobStatus): status is RunningJobStatus {
  return isRunningStatus(status, 'job')
}

export function jobStatusVisual(status: JobStatus): { color: string; pulsing: boolean; label: string } {
  const visual = statusLabel(status, 'job')
  return { color: visual.color, pulsing: Boolean(visual.pulsing), label: visual.textKey }
}

export function assetStatusVisual(status: AssetStatus): { color: string; label: string } {
  const visual = statusLabel(status, 'asset')
  return { color: visual.color, label: visual.textKey }
}

export function cascadeStateVisual(state: CascadeState): { color: string; pulsing: boolean; label: string } {
  const visual = statusLabel(state, 'cascade')
  return { color: visual.color, pulsing: Boolean(visual.pulsing), label: visual.textKey }
}

export function parseAssetPayload(payload: string): { parsed: unknown; quality?: number; formatted: string } {
  try {
    const parsed = JSON.parse(payload) as unknown
    const rawQuality = parsed && typeof parsed === 'object' && 'quality' in parsed ? (parsed as { quality?: unknown }).quality : undefined
    const quality = qualityNumber(rawQuality)

    return {
      parsed,
      quality: typeof quality === 'number' && Number.isFinite(quality) ? quality : undefined,
      formatted: JSON.stringify(parsed, null, 2),
    }
  } catch {
    return { parsed: null, formatted: payload }
  }
}

function progressPercent(job: JobView) {
  if (job.total_segments <= 0) return 0
  return Math.min(100, Math.round((job.current_seg / job.total_segments) * 100))
}

function sortJobs(a: JobView, b: JobView) {
  if (a.chapter_no !== b.chapter_no) return a.chapter_no - b.chapter_no
  return (b.updated_at ?? 0) - (a.updated_at ?? 0)
}

const incompleteChapterStatuses = new Set(['generating', 'streaming', 'writing', 'drafting'])

export function isCompletedChapter(chapter: Chapter) {
  const status = typeof chapter.status === 'string' ? chapter.status.trim().toLowerCase() : ''
  if (incompleteChapterStatuses.has(status)) return false
  if (typeof chapter.content === 'string' && chapter.content.trim()) return true
  return status === 'done' || status === 'completed' || status === 'complete' || status === 'finished'
}

function sortChapters(a: Chapter, b: Chapter) {
  if (a.chapter_number !== b.chapter_number) return a.chapter_number - b.chapter_number
  return a.title.localeCompare(b.title)
}

export function filterBoardTextChapters(chapters: Chapter[], cascadeStates: Map<string, CascadeState>) {
  return chapters
    .filter((chapter) => isCompletedChapter(chapter))
    .filter((chapter) => {
      const state = cascadeStates.get(chapter.id)
      return state === 'running' || state === 'awaiting_confirm'
    })
    .sort(sortChapters)
}

function projectAwaitingTotal(project: PulseSummaryProject) {
  return summaryStationColumns.reduce((total, station) => total + (project.stations[station.key]?.awaiting_confirm ?? 0), 0)
}

function projectGeneratingTotal(project: PulseSummaryProject) {
  return summaryStationColumns.reduce((total, station) => total + (project.stations[station.key]?.generating ?? 0), 0)
}

export function sortFactorySummaryProjects(projects: PulseSummaryProject[]) {
  return [...projects].sort((a, b) => {
    const awaitingDiff = projectAwaitingTotal(b) - projectAwaitingTotal(a)
    if (awaitingDiff !== 0) return awaitingDiff
    const generatingDiff = projectGeneratingTotal(b) - projectGeneratingTotal(a)
    if (generatingDiff !== 0) return generatingDiff
    return a.title.localeCompare(b.title)
  })
}

function formatQuality(quality?: number) {
  if (quality === undefined) return null
  if (quality >= 0 && quality <= 1) return formatCount(quality * 100)
  return formatCount(quality)
}

function qualityNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  if (!value || typeof value !== 'object') return undefined
  const record = value as Record<string, unknown>
  for (const key of ['score', 'overall', 'quality']) {
    const parsed = qualityNumber(record[key])
    if (parsed !== undefined) return parsed
  }
  return undefined
}

function summarizeQualityValue(value: unknown, t: TFunction) {
  const quality = qualityNumber(value)
  if (quality !== undefined) return formatQuality(quality) ?? formatNumber(quality)
  if (!value || typeof value !== 'object') return null

  const record = value as Record<string, unknown>
  const parts: string[] = []
  if (typeof record.status === 'string' && record.status.trim()) {
    parts.push(t('board.qualityStatus', { status: record.status.trim() }))
  }
  if (typeof record.layer === 'string' && record.layer.trim()) {
    parts.push(t('board.qualityLayer', { layer: record.layer.trim() }))
  }
  const degraded = qualityNumber(record.degraded_count)
  const total = qualityNumber(record.total_segments)
  if (degraded !== undefined && total !== undefined) {
    parts.push(t('board.qualityDegradedRatio', { degraded: formatCount(degraded), total: formatCount(total) }))
  } else if (degraded !== undefined) {
    parts.push(t('board.qualityDegraded', { count: formatCount(degraded) }))
  }
  return parts.length ? parts.join(' · ') : t('board.qualityRecorded')
}

function summarizeValue(key: (typeof payloadSummaryKeys)[number], value: unknown, t: TFunction) {
  if (key === 'quality' || key === 'score') {
    return summarizeQualityValue(value, t)
  }
  if (typeof value === 'number') {
    if (key === 'duration_ms') return formatDuration(value)
    if (key === 'duration') return formatDuration(value * 1000)
    if (key === 'word_count' || key === 'panel_count' || key === 'scene_count') return formatCount(value)
    return formatNumber(value)
  }
  if (typeof value === 'string') return value.length > 80 ? `${value.slice(0, 80)}...` : value
  if (typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

function chapterDisplay(chapter: Chapter | undefined, fallback: string, t: TFunction) {
  if (!chapter) return fallback
  const title = chapter.title.trim()
  if (!title) return t('board.chapter', { number: formatCount(chapter.chapter_number) })
  return t('board.chapterTitle', { number: formatCount(chapter.chapter_number), title })
}

function payloadSummary(parsed: unknown, t: TFunction) {
  if (!parsed || typeof parsed !== 'object') return []
  const payload = parsed as Record<string, unknown>
  return payloadSummaryKeys
    .filter((key) => key in payload)
    .map((key) => ({ key, value: summarizeValue(key, payload[key], t) }))
    .filter((entry) => entry.value !== undefined && entry.value !== null)
}

type AssetMediaPreview =
  | { kind: 'image'; chapterId: string }
  | { kind: 'audio'; chapterId: string; src: string }
  | { kind: 'video'; chapterId: string; src: string }
  | { kind: 'screenplay' }
  | { kind: 'storyboard' }
  | { kind: 'none'; reason: 'missing_chapter' | 'unsupported' }

function mediaSrc(chapterId: string, suffix: string) {
  return `${API_BASE}/v1/chapters/${encodeURIComponent(chapterId)}/${suffix}`
}

function imageRawSrc(chapterId: string, imageId: string) {
  return mediaSrc(chapterId, `images/${encodeURIComponent(imageId)}/raw`)
}

export function assetMediaPreview(asset: Asset): AssetMediaPreview {
  if (asset.asset_type === 'screenplay') return { kind: 'screenplay' }
  if (asset.asset_type === 'storyboard') return { kind: 'storyboard' }
  if (!asset.chapter_id) return { kind: 'none', reason: 'missing_chapter' }
  if (asset.asset_type === 'panel_image') return { kind: 'image', chapterId: asset.chapter_id }
  if (asset.asset_type === 'audio') return { kind: 'audio', chapterId: asset.chapter_id, src: mediaSrc(asset.chapter_id, 'audio/raw') }
  if (asset.asset_type === 'video') return { kind: 'video', chapterId: asset.chapter_id, src: mediaSrc(asset.chapter_id, 'video/raw') }
  return { kind: 'none', reason: 'unsupported' }
}

export function buildTextAssetDownload(asset: Asset): { filename: string; mimeType: string; content: string } | null {
  if (asset.asset_type === 'screenplay') {
    const screenplay = parseScreenplayPayload(asset)
    const sceneText = screenplay?.scenes?.map(formatScreenplayScene).filter(Boolean).join('\n\n') ?? ''
    return {
      filename: `screenplay-${asset.id}-v${asset.version}.txt`,
      mimeType: 'text/plain;charset=utf-8',
      content: sceneText || asset.payload,
    }
  }
  if (asset.asset_type === 'storyboard') {
    const panels = parseStoryboardPanels(asset)
    let fallbackPayload: unknown = asset.payload
    try {
      fallbackPayload = JSON.parse(asset.payload)
    } catch {
      fallbackPayload = asset.payload
    }
    return {
      filename: `storyboard-${asset.id}-v${asset.version}.json`,
      mimeType: 'application/json;charset=utf-8',
      content: JSON.stringify(panels.length ? panels : fallbackPayload, null, 2),
    }
  }
  return null
}

function formatScreenplayScene(scene: ScreenplayScene) {
  return [
    scene.scene_id,
    scene.setting,
    scene.action,
    scene.narration,
    ...(scene.dialogues ?? []).map((dialogue) => {
      const content = dialogue.content?.trim()
      if (!content) return ''
      const character = dialogue.character?.trim()
      return character ? `${character}: ${content}` : content
    }),
  ]
    .map((line) => line?.trim())
    .filter(Boolean)
    .join('\n')
}

export function getPreviewableChapterImages(images: ChapterImage[]) {
  return images.filter((image) => image.status === 'done')
}

function mediaDownloadName(asset: Asset, extension: string) {
  return `${asset.asset_type}-${asset.id}-v${asset.version}.${extension}`
}

function AssetDownloadButton({ asset, href, extension }: { asset: Asset; href?: string; extension?: string }) {
  const { t } = useTranslation()
  const textDownload = buildTextAssetDownload(asset)
  if (href && extension) {
    return (
      <Button type="button" variant="outline" size="sm" asChild>
        {/* Cross-origin responses may ignore download; the link still opens the asset URL. */}
        <a href={href} download={mediaDownloadName(asset, extension)} target="_blank" rel="noreferrer">
          <Download className="size-3.5" />
          {t('board.assetDownload')}
        </a>
      </Button>
    )
  }
  if (!textDownload) return null
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => downloadText(textDownload.filename, textDownload.mimeType, textDownload.content)}
    >
      <Download className="size-3.5" />
      {t('board.assetDownload')}
    </Button>
  )
}

function sortAssets(a: Asset, b: Asset) {
  const chapterCompare = (a.chapter_id ?? '').localeCompare(b.chapter_id ?? '')
  if (chapterCompare !== 0) return chapterCompare
  if (a.stage !== b.stage) return a.stage - b.stage
  if (a.version !== b.version) return b.version - a.version
  return b.updated_at.localeCompare(a.updated_at)
}

function handleCardKeyDown(event: KeyboardEvent<HTMLElement>, onOpen: () => void) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    onOpen()
  }
}

function statusStyle(color: string): CSSProperties {
  return {
    color,
    borderColor: color,
    backgroundColor: `color-mix(in oklch, ${color} 12%, transparent)`,
  }
}

function StatusBadge({ color, pulsing, children }: { color: string; pulsing?: boolean; children: ReactNode }) {
  return (
    <Badge variant="outline" className="gap-2 border text-xs font-medium" style={statusStyle(color)}>
      <span className={pulsing ? 'nf-pulse size-2 rounded-full' : 'size-2 rounded-full'} style={{ background: color }} />
      {children}
    </Badge>
  )
}

function CascadeControls({
  chapterId,
  loading,
  onContinue,
  onStop,
}: {
  chapterId: string
  loading: boolean
  onContinue: (chapterId: string) => void
  onStop: (chapterId: string) => void
}) {
  const { t } = useTranslation()

  const stopEvent = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
  }

  return (
    <div className="flex gap-2" onClick={stopEvent}>
      <Button type="button" variant="outline" size="sm" className="h-8 flex-1" disabled={loading} onClick={() => onContinue(chapterId)}>
        {loading ? <Loader2 className="size-3.5 animate-spin" /> : <Play className="size-3.5" />}
        {t('cascade.actions.continue')}
      </Button>
      <Button type="button" variant="outline" size="sm" className="h-8 flex-1" disabled={loading} onClick={() => onStop(chapterId)}>
        {loading ? <Loader2 className="size-3.5 animate-spin" /> : <Square className="size-3.5" />}
        {t('cascade.actions.stop')}
      </Button>
    </div>
  )
}

function EmptyState({ description, className }: { description: string; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground', className)}>
      <Inbox className="size-8 opacity-60" />
      <span>{description}</span>
    </div>
  )
}

function AlertBox({ type, message, description }: { type: 'error' | 'warning'; message: string; description?: string }) {
  const tone = type === 'error' ? 'border-destructive/40 bg-destructive/10 text-destructive' : 'text-foreground'
  const style =
    type === 'warning'
      ? ({
          borderColor: 'color-mix(in oklch, var(--nf-st-review) 40%, transparent)',
          backgroundColor: 'color-mix(in oklch, var(--nf-st-review) 10%, transparent)',
        } satisfies CSSProperties)
      : undefined

  return (
    <div className={`rounded-md border p-3 text-sm ${tone}`} role="alert" style={style}>
      <div className="flex gap-2">
        <AlertCircle className="mt-0.5 size-4 shrink-0" />
        <div className="min-w-0">
          <div>{message}</div>
          {description ? <div className="mt-1 text-muted-foreground">{description}</div> : null}
        </div>
      </div>
    </div>
  )
}

function LoadingOverlay({ loading, children }: { loading: boolean; children: ReactNode }) {
  return (
    <div className="relative">
      {loading ? (
        <div className="absolute inset-0 z-10 flex min-h-24 items-center justify-center rounded-md bg-background/60">
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      ) : null}
      <div className={loading ? 'pointer-events-none opacity-60' : undefined}>{children}</div>
    </div>
  )
}

function CopyableText({ children }: { children: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const onCopy = () => {
    const write = navigator.clipboard?.writeText(children)
    if (!write) return
    void write.then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1000)
    })
  }

  return (
    <button
      type="button"
      className="group inline-flex max-w-full items-center gap-2 rounded-sm text-left font-mono text-sm hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={onCopy}
      title={copied ? t('common.copied') : t('common.copy')}
    >
      <span className="truncate">{children}</span>
      <Clipboard className="size-3.5 shrink-0 opacity-45 group-hover:opacity-80" />
    </button>
  )
}

const JobCard = memo(function JobCard({ job, onOpen }: { job: JobView; onOpen: () => void }) {
  const { t } = useTranslation()
  const visual = jobStatusVisual(job.status)
  const isRunning = visual.pulsing

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => handleCardKeyDown(event, onOpen)}
      className="cursor-pointer gap-0 rounded-lg border-[var(--nf-hairline)] bg-[var(--nf-surface-1)] py-0 transition-colors hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <CardContent className="flex h-full flex-col gap-3 p-3.5">
        <div className="flex items-center justify-between gap-3">
          <span className="font-semibold">{t('board.chapter', { number: formatCount(job.chapter_no) })}</span>
          <StatusBadge color={visual.color} pulsing={visual.pulsing}>
            {t(visual.label)}
          </StatusBadge>
        </div>

        <div className="flex flex-col gap-1.5">
          <Progress
            value={progressPercent(job)}
            className="bg-[var(--nf-surface-3)]"
            style={{ '--nf-progress-color': visual.color } as CSSProperties}
          />
          <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
            <span>
              {isRunning
                ? t('board.runningSegments', { current: formatCount(job.current_seg), total: formatCount(job.total_segments) })
                : t('board.segments', { current: formatCount(job.current_seg), total: formatCount(job.total_segments) })}
            </span>
            <span className="nf-mono">{formatPercent(progressPercent(job) / 100)}</span>
          </div>
        </div>

        <div className="mt-auto flex flex-wrap justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm text-muted-foreground">{t('board.attempts')}</span>
            <span className="nf-mono text-sm">
            {formatCount(job.attempts)}/{formatCount(job.max_attempts)}
            </span>
          </div>
          <div className="flex flex-col gap-0.5 text-right">
            <span className="text-sm text-muted-foreground">{t('board.updatedAt')}</span>
            <span className="nf-mono text-sm">{formatDate(job.updated_at)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
})

function AssetCard({
  asset,
  chaptersById,
  cascadeLoadingChapterId,
  onCascadeContinue,
  onCascadeStop,
  onOpen,
}: {
  asset: Asset
  chaptersById: ChapterMap
  cascadeLoadingChapterId?: string
  onCascadeContinue: (chapterId: string) => void
  onCascadeStop: (chapterId: string) => void
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const visual = assetStatusVisual(asset.status)
  const cascadeVisual = asset.cascade_state ? cascadeStateVisual(asset.cascade_state) : null
  const payload = parseAssetPayload(asset.payload)
  const quality = formatQuality(payload.quality)
  const chapterLabel = asset.chapter_id ? chapterDisplay(chaptersById.get(asset.chapter_id), t('board.chapterUnknown'), t) : t('board.chapterUnknown')

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => handleCardKeyDown(event, onOpen)}
      className="cursor-pointer gap-0 rounded-lg border-[var(--nf-hairline)] bg-[var(--nf-surface-1)] py-0 transition-colors hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <CardContent className="flex flex-col gap-2.5 p-3.5">
        <div className="flex items-center justify-between gap-3">
          <span className="font-semibold">{chapterLabel}</span>
          <StatusBadge color={visual.color}>{t(visual.label)}</StatusBadge>
        </div>

        {asset.cascade_state && cascadeVisual ? (
          <StatusBadge color={cascadeVisual.color} pulsing={cascadeVisual.pulsing}>
            {t(cascadeVisual.label)}
          </StatusBadge>
        ) : null}

        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-muted-foreground">{t('board.quality')}</span>
          <span className="nf-mono">{quality ?? t('board.noQuality')}</span>
        </div>

        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-muted-foreground">{t('board.stageVersion')}</span>
          <span className="nf-mono">{t('board.stageVersionValue', { stage: formatCount(asset.stage), version: formatCount(asset.version) })}</span>
        </div>

        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-muted-foreground">{t('board.updatedAt')}</span>
          <span className="nf-mono">{formatDate(asset.updated_at)}</span>
        </div>

        {asset.cascade_state === 'awaiting_confirm' && asset.chapter_id ? (
          <CascadeControls
            chapterId={asset.chapter_id}
            loading={cascadeLoadingChapterId === asset.chapter_id}
            onContinue={onCascadeContinue}
            onStop={onCascadeStop}
          />
        ) : null}
      </CardContent>
    </Card>
  )
}

function BoardColumn({
  title,
  count,
  aside,
  children,
  className,
}: {
  title: string
  count: number
  aside?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn('rounded-lg border border-[var(--nf-hairline)] bg-[var(--nf-surface-1)] p-3', className ?? 'w-56 min-w-56')}>
      <div className="flex w-full flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <span className="font-semibold">{title}</span>
          <div className="flex shrink-0 items-center gap-2">
            {aside}
            <Badge variant="secondary" className="nf-mono">
              {formatCount(count)}
            </Badge>
          </div>
        </div>
        <div className="flex w-full flex-col gap-3">{children}</div>
      </div>
    </section>
  )
}

function BoardStage({
  marker,
  label,
  hint,
  children,
}: {
  marker: string
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-baseline gap-2.5 border-b border-[var(--nf-hairline)] pb-2">
        <span className="nf-mono text-sm font-semibold text-primary">{marker}</span>
        <h3 className="text-sm font-semibold tracking-wide">{label}</h3>
        {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
      </div>
      {children}
    </section>
  )
}

export function FactoryOverviewMatrix({
  projects,
  loading,
  error,
  onSelectProject,
}: {
  projects: PulseSummaryProject[]
  loading: boolean
  error: string | null
  onSelectProject: (projectId: string) => void
}) {
  const { t } = useTranslation()
  const sortedProjects = useMemo(() => sortFactorySummaryProjects(projects), [projects])
  const interventionSummary = useMemo(() => {
    const projectsAwaiting = sortedProjects
      .map((project) => ({ project, awaiting: projectAwaitingTotal(project) }))
      .filter((entry) => entry.awaiting > 0)
    return {
      totalAwaiting: projectsAwaiting.reduce((total, entry) => total + entry.awaiting, 0),
      projectCount: projectsAwaiting.length,
      firstProjectId: projectsAwaiting[0]?.project.project_id,
    }
  }, [sortedProjects])

  return (
    <section className="rounded-lg border border-[var(--nf-hairline)] bg-[var(--nf-surface-1)]">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--nf-hairline)] px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <Factory className="size-4 text-primary" />
          <h3 className="truncate font-semibold">{t('overview.matrixTitle')}</h3>
          <Badge variant="secondary" className="nf-mono">
            {formatCount(projects.length)}
          </Badge>
        </div>
        {loading ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
      </div>

      <div className="p-4">
        {error ? <AlertBox type="warning" message={t('overview.loadFailed')} description={error} /> : null}
        {!error && interventionSummary.totalAwaiting > 0 ? (
          <div
            className="mb-4 flex flex-col gap-3 rounded-md border px-3 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"
            style={{
              borderColor: 'color-mix(in oklch, var(--nf-st-review) 45%, transparent)',
              backgroundColor: 'color-mix(in oklch, var(--nf-st-review) 12%, transparent)',
            }}
          >
            <span className="font-medium">
              {t('overview.intervention.message', {
                projectCount: formatCount(interventionSummary.projectCount),
                chapterCount: formatCount(interventionSummary.totalAwaiting),
              })}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 self-start border-[var(--nf-st-review)]/60 bg-background/70 sm:self-auto"
              onClick={() => {
                if (interventionSummary.firstProjectId) onSelectProject(interventionSummary.firstProjectId)
              }}
            >
              {t('overview.intervention.action')}
              <ArrowRight className="size-3.5" />
            </Button>
          </div>
        ) : null}
        {!error && !sortedProjects.length ? (
          <EmptyState description={t('overview.empty')} />
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[1040px]">
              <div className="grid grid-cols-[minmax(14rem,1.25fr)_repeat(6,minmax(8rem,1fr))] gap-2 px-3 pb-2 text-xs font-medium text-muted-foreground">
                <span>{t('overview.project')}</span>
                {summaryStationColumns.map((station) => (
                  <span key={station.key}>{t(station.label)}</span>
                ))}
              </div>
              <div className="flex flex-col gap-2">
                {sortedProjects.map((project) => {
                  const awaitingTotal = projectAwaitingTotal(project)
                  const generatingTotal = projectGeneratingTotal(project)
                  return (
                    <button
                      key={project.project_id}
                      type="button"
                      className={cn(
                        'grid w-full grid-cols-[minmax(14rem,1.25fr)_repeat(6,minmax(8rem,1fr))] items-stretch gap-2 rounded-md border bg-background/40 px-3 py-2 text-left transition-colors hover:border-primary/60 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        // deepseek Phase2 紧急度: 有待确认的作品左边框点亮(待确认色)+底色, 浮顶又显眼
                        awaitingTotal > 0
                          ? 'border-[var(--nf-st-review)]/60 border-l-4 bg-[var(--nf-st-review)]/[0.06]'
                          : 'border-[var(--nf-hairline)]',
                      )}
                      onClick={() => onSelectProject(project.project_id)}
                    >
                      <span className="flex min-w-0 flex-col justify-center gap-1">
                        <span className="truncate font-medium">{project.title}</span>
                        <span className="text-xs text-muted-foreground">
                          {t('overview.projectTotals', {
                            awaiting: formatCount(awaitingTotal),
                            generating: formatCount(generatingTotal),
                          })}
                        </span>
                      </span>
                      {summaryStationColumns.map((station) => {
                        const counts = project.stations[station.key]
                        const awaiting = counts?.awaiting_confirm ?? 0
                        const generating = counts?.generating ?? 0
                        const running = counts?.running ?? 0
                        return (
                          <span
                            key={station.key}
                            className={cn(
                              'flex min-w-0 flex-col gap-1.5 rounded-md border px-2 py-2 text-xs',
                              // deepseek Phase2 紧急度: 待确认>0 用待确认色(琥珀)而非中性强调, 一眼定位需干预的格
                              awaiting > 0
                                ? 'border-[var(--nf-st-review)]/50 bg-[var(--nf-st-review)]/[0.12]'
                                : 'border-transparent bg-[var(--nf-surface-2)]',
                            )}
                          >
                            <span className="flex items-center justify-between gap-2">
                              <span className="text-muted-foreground">{t('overview.generating')}</span>
                              <span className="nf-mono font-medium">⚡ {formatCount(generating)}</span>
                            </span>
                            <span className="flex items-center justify-between gap-2">
                              <span className={awaiting > 0 ? 'font-medium text-foreground' : 'text-muted-foreground'}>
                                {t('overview.awaitingConfirm')}
                              </span>
                              {/* deepseek Phase2: 待确认>0 计数克制脉搏, 引导注意力 */}
                              <span className={cn('nf-mono font-medium', awaiting > 0 && 'animate-pulse')}>
                                🟡 {formatCount(awaiting)}
                              </span>
                            </span>
                            <span className="flex items-center justify-between gap-2">
                              <span className="text-muted-foreground">{t('overview.running')}</span>
                              <span className="nf-mono font-medium">{formatCount(running)}</span>
                            </span>
                          </span>
                        )
                      })}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

function ChapterTextCard({
  chapter,
  cascadeState,
  cascadeLoadingChapterId,
  onCascadeContinue,
  onCascadeStop,
  onOpen,
}: {
  chapter: Chapter
  cascadeState?: CascadeState | null
  cascadeLoadingChapterId?: string
  onCascadeContinue: (chapterId: string) => void
  onCascadeStop: (chapterId: string) => void
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const title = chapterDisplay(chapter, t('board.chapterUnknown'), t)
  const wordCount = typeof chapter.word_count === 'number' && Number.isFinite(chapter.word_count) ? chapter.word_count : null
  const cascadeVisual = cascadeState ? cascadeStateVisual(cascadeState) : null

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => handleCardKeyDown(event, onOpen)}
      className="cursor-pointer gap-0 rounded-lg border-[var(--nf-hairline)] bg-[var(--nf-surface-1)] py-0 transition-colors hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <CardContent className="flex flex-col gap-2.5 p-3.5">
        <div className="font-semibold">{title}</div>
        {cascadeState && cascadeVisual ? (
          <StatusBadge color={cascadeVisual.color} pulsing={cascadeVisual.pulsing}>
            {t(cascadeVisual.label)}
          </StatusBadge>
        ) : null}
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-muted-foreground">{t('board.wordCount')}</span>
          <span className="nf-mono">{wordCount === null ? t('board.noWordCount') : formatCount(wordCount)}</span>
        </div>
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-muted-foreground">{t('board.updatedAt')}</span>
          <span className="nf-mono">{formatDate(chapter.updated_at)}</span>
        </div>
        {cascadeState === 'awaiting_confirm' ? (
          <CascadeControls
            chapterId={chapter.id}
            loading={cascadeLoadingChapterId === chapter.id}
            onContinue={onCascadeContinue}
            onStop={onCascadeStop}
          />
        ) : null}
      </CardContent>
    </Card>
  )
}

function AssetPreview({ asset }: { asset: Asset }) {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const preview = assetMediaPreview(asset)
  const [images, setImages] = useState<ChapterImage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (preview.kind !== 'image') {
      setImages([])
      setLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)
    setImages([])

    chapterApi
      .images(preview.chapterId)
      .then((nextImages) => {
        if (!cancelled) setImages(getPreviewableChapterImages(nextImages))
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(friendlyMessage(err, t('common.serviceError')))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [preview.kind, preview.kind === 'image' ? preview.chapterId : '', t])

  if (preview.kind === 'none') {
    if (preview.reason === 'unsupported') return null
    return <EmptyState description={t('board.noPreviewAsset')} />
  }

  if (preview.kind === 'screenplay') {
    const screenplay = parseScreenplayPayload(asset)
    return (
      <div className="flex w-full flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">{t('board.assetPreview')}</span>
          <AssetDownloadButton asset={asset} />
        </div>
        <ScreenplayView scenes={screenplay?.scenes ?? []} />
      </div>
    )
  }

  if (preview.kind === 'storyboard') {
    return (
      <div className="flex w-full flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">{t('board.assetPreview')}</span>
          <AssetDownloadButton asset={asset} />
        </div>
        <StoryboardView panels={parseStoryboardPanels(asset)} />
      </div>
    )
  }

  if (preview.kind === 'audio') {
    return (
      <div className="flex w-full flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">{t('board.assetPreview')}</span>
          <AssetDownloadButton asset={asset} href={preview.src} extension="mp3" />
        </div>
        <audio controls src={preview.src} className="w-full">
          {t('board.mediaUnsupported')}
        </audio>
      </div>
    )
  }

  if (preview.kind === 'video') {
    return (
      <div className="flex w-full flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">{t('board.assetPreview')}</span>
          <AssetDownloadButton asset={asset} href={preview.src} extension="mp4" />
        </div>
        <AssetProvenanceNotice payload={asset.payload} />
 <video controls src={preview.src} className="max-h-80 w-full rounded-lg bg-[var(--nf-surface-2)]">
          {t('board.mediaUnsupported')}
        </video>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex w-full flex-col gap-2">
        <span className="text-sm text-muted-foreground">{t('board.assetPreview')}</span>
        <Loader2 className="size-5 animate-spin text-primary" />
      </div>
    )
  }

  if (error) return <AlertBox type="warning" message={t('board.previewLoadFailed')} description={error} />

  if (!images.length) return <EmptyState description={t('board.noPreviewAsset')} />
  const paged = pageItems(images, page, PAGE_SIZE)

  return (
    <div className="flex w-full flex-col gap-2">
      <span className="text-sm text-muted-foreground">{t('board.assetPreview')}</span>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(112px,1fr))] gap-3">
        {paged.pageItems.map((image) => (
          <div key={image.id} className="flex flex-col gap-1.5">
            <a href={imageRawSrc(preview.chapterId, image.id)} target="_blank" rel="noreferrer" className="block">
              <img
                alt={t('board.chapterImageAlt')}
                src={imageRawSrc(preview.chapterId, image.id)}
                loading="lazy"
                className="h-28 w-full rounded-lg border border-[var(--nf-hairline)] bg-[var(--nf-surface-2)] object-cover"
              />
            </a>
            <Button type="button" variant="outline" size="sm" asChild>
              <a
                href={imageRawSrc(preview.chapterId, image.id)}
                download={`panel-image-${image.id}.png`}
                target="_blank"
                rel="noreferrer"
              >
                <Download className="size-3.5" />
                {t('board.assetDownload')}
              </a>
            </Button>
          </div>
        ))}
      </div>
      {paged.pageCount > 1 ? (
        <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
          <Button type="button" variant="outline" size="sm" disabled={paged.page <= 0} onClick={() => setPage(paged.page - 1)}>
            {t('pagination.previous')}
          </Button>
          <span className="text-sm text-muted-foreground">
            {t('pagination.pageWithUnit', { page: paged.page + 1, total: paged.pageCount })}
          </span>
          <span className="text-sm text-muted-foreground">
            {t('pagination.totalImages', { total: images.length })}
          </span>
          <Button type="button" variant="outline" size="sm" disabled={paged.page >= paged.pageCount - 1} onClick={() => setPage(paged.page + 1)}>
            {t('pagination.next')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function AssetDetailDrawer({
  selection,
  assets,
  chaptersById,
  onClose,
}: {
  selection: DrawerSelection
  assets: Asset[]
  chaptersById: ChapterMap
  onClose: () => void
}) {
  const { t } = useTranslation()
  const open = selection !== null
  const title =
    selection?.kind === 'job'
      ? t('board.jobDrawerTitle', { chapter: formatCount(selection.job.chapter_no) })
      : selection?.kind === 'asset'
        ? t('board.assetDrawerTitle', { type: t(`assetType.${selection.asset.asset_type}`) })
        : ''

  const jobAssets =
    selection?.kind === 'job' && selection.job.result_chapter_id
      ? assets.filter((asset) => asset.chapter_id === selection.job.result_chapter_id).sort(sortAssets)
      : []
  const detailAssets = selection?.kind === 'asset' ? [selection.asset] : jobAssets

  return (
    <Sheet open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) onClose()
    }}>
      <SheetContent className="w-full gap-0 overflow-y-auto p-0 sm:max-w-[520px]">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-4 p-6">
          {selection?.kind === 'job' ? (
            <div className="mb-1 flex w-full flex-col gap-2 text-sm">
              <span className="text-muted-foreground">{t('board.jobId')}</span>
              <span className="nf-mono break-all">{selection.job.id}</span>
              <span className="text-muted-foreground">{t('board.createdAt')}</span>
              <span className="nf-mono">{formatDateTime(selection.job.created_at)}</span>
              <span className="text-muted-foreground">{t('board.updatedAt')}</span>
              <span className="nf-mono">{formatDateTime(selection.job.updated_at)}</span>
              {selection.job.result_chapter_id ? (
                <>
                  <span className="text-muted-foreground">{t('board.resultChapter')}</span>
                  <span className="nf-mono break-all">{selection.job.result_chapter_id}</span>
                </>
              ) : null}
            </div>
          ) : null}

          {selection?.kind === 'job' ? <JobContextWarnings jobId={selection.job.id} /> : null}

          {!detailAssets.length ? (
            <EmptyState description={t('board.emptyDrawerAssets')} />
          ) : (
            <div className="flex w-full flex-col gap-4">
              {detailAssets.map((asset) => {
                const visual = assetStatusVisual(asset.status)
                const payload = parseAssetPayload(asset.payload)
                const quality = formatQuality(payload.quality)
                const summary = payloadSummary(payload.parsed, t)

                return (
                  <Card key={asset.id} className="gap-0 rounded-lg border-[var(--nf-hairline)] py-0">
                    <CardContent className="flex w-full flex-col gap-3 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-semibold">{t(`assetType.${asset.asset_type}`)}</span>
                        <StatusBadge color={visual.color}>{t(visual.label)}</StatusBadge>
                      </div>

                      <div className="flex w-full flex-col gap-1 text-sm">
                        <span className="text-muted-foreground">{t('board.assetId')}</span>
                        <CopyableText>{asset.id}</CopyableText>
                        {asset.chapter_id ? (
                          <>
                            <span className="text-muted-foreground">{t('board.chapterAsset')}</span>
                            <span>{chapterDisplay(chaptersById.get(asset.chapter_id), t('board.chapterUnknown'), t)}</span>
                          </>
                        ) : null}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Badge variant="secondary" className="nf-mono">
                          {t('board.stageValue', { stage: formatCount(asset.stage) })}
                        </Badge>
                        <Badge variant="secondary" className="nf-mono">
                          {t('board.versionValue', { version: formatCount(asset.version) })}
                        </Badge>
                        {quality !== null ? <Badge variant="secondary" className="nf-mono">Q{quality}</Badge> : null}
                      </div>

                      <div className="flex w-full flex-col gap-1 text-sm">
                        <span className="text-muted-foreground">{t('board.createdAt')}</span>
                        <span className="nf-mono">{formatDateTime(asset.created_at)}</span>
                        <span className="text-muted-foreground">{t('board.updatedAt')}</span>
                        <span className="nf-mono">{formatDateTime(asset.updated_at)}</span>
                      </div>

                      <AssetPreview asset={asset} />

                      {summary.length ? (
                        <>
                          <Separator className="my-1" />
                          <div className="flex w-full flex-col gap-2">
                            {summary.map((entry) => (
                              <div key={entry.key} className="flex w-full items-start justify-between gap-4 text-sm">
                                <span className="text-muted-foreground">{t(`payload.${entry.key}`, { defaultValue: entry.key })}</span>
                                <span className="max-w-[300px] text-right">{entry.value}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : null}

                      <details className="rounded-lg border border-[var(--nf-hairline)] bg-[var(--nf-surface-2)] p-3 text-sm">
                        <summary className="cursor-pointer text-muted-foreground">{t('board.developerDetails')}</summary>
                        <pre className="nf-mono mt-3 max-h-[280px] overflow-auto whitespace-pre-wrap break-words text-sm">
                          {payload.formatted}
                        </pre>
                      </details>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default function JobBoard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const { projects, current, loading: projectLoading, error: projectError, load, select } = useProjectStore()
  const currentBranchId = useUiStore((s) => s.currentBranchId)
  const requestSelectChapter = useUiStore((s) => s.requestSelectChapter)
  const {
    jobs,
    loading: jobLoading,
    error: jobError,
    loadList,
    subscribe,
    unsubscribe,
    unsubscribeAll,
  } = useJobStore()
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [assets, setAssets] = useState<Asset[]>([])
  const [assetLoading, setAssetLoading] = useState(false)
  const [assetError, setAssetError] = useState<string | null>(null)
  const [chapters, setChapters] = useState<Chapter[]>([])
  const [chapterLoading, setChapterLoading] = useState(false)
  const [chapterError, setChapterError] = useState<string | null>(null)
  const [drawerSelection, setDrawerSelection] = useState<DrawerSelection>(null)
  const [submitDrawerOpen, setSubmitDrawerOpen] = useState(false)
  const [boardView, setBoardView] = useState<BoardViewMode>(initialBoardView)
  const [assetColumnPages, setAssetColumnPages] = useState<Record<StationAssetType, number>>({
    screenplay: 0,
    storyboard: 0,
    panel_image: 0,
    audio: 0,
    video: 0,
  })
  useEffect(() => {
    try {
      localStorage.setItem(BOARD_VIEW_LS_KEY, boardView)
    } catch {
      // localStorage 不可用 → 忽略(仅丢失记忆, 不影响功能)
    }
  }, [boardView])
  const subscribedJobIdsRef = useRef<Set<string>>(new Set())
  const observedRunningJobIdsRef = useRef<Set<string>>(new Set())
  const tRef = useRef(t)

  tRef.current = t

  useEffect(() => {
    if (user?.user_id) void load(user.user_id)
  }, [load, user?.user_id])

  const summaryQuery = useQuery({
    queryKey: ['pulse', 'summary'],
    queryFn: pulseApi.summary,
    enabled: Boolean(user?.user_id),
    // 工厂总览刷新: 收紧到 10s 更接近实时 (deepseek 分析: 10-30s 够, 不做秒级 SSE 基建)。
    refetchInterval: 10_000,
  })

  useEffect(() => {
    if (!projects.length) return
    if (selectedProjectId && projects.some((project) => project.id === selectedProjectId)) return

    const nextProjectId = current && projects.some((project) => project.id === current.id) ? current.id : projects[0].id
    setSelectedProjectId(nextProjectId)
    if (current?.id !== nextProjectId) void select(nextProjectId)
  }, [current, projects, select, selectedProjectId])

  useEffect(() => {
    if (!selectedProjectId) return
    unsubscribeAll()
    subscribedJobIdsRef.current = new Set()
    void loadList(selectedProjectId, { branchId: currentBranchId })
    return () => {
      unsubscribeAll()
      subscribedJobIdsRef.current = new Set()
    }
  }, [currentBranchId, loadList, selectedProjectId, unsubscribeAll])

  const loadChapters = useCallback(async (projectId: string, branchId: string, isCancelled: () => boolean = () => false) => {
    setChapterLoading(true)
    setChapterError(null)
    setChapters([])
    try {
      const nextChapters = await chapterApi.list(projectId, branchId)
      if (!isCancelled()) setChapters(nextChapters)
    } catch (error: unknown) {
      if (!isCancelled()) setChapterError(friendlyMessage(error, tRef.current('common.serviceError')))
    } finally {
      if (!isCancelled()) setChapterLoading(false)
    }
  }, [])

  const loadAssets = useCallback(async (projectId: string, isCancelled: () => boolean = () => false) => {
    setAssetLoading(true)
    setAssetError(null)
    setAssets([])
    try {
      const nextAssets = await projectApi.assets(projectId)
      if (!isCancelled()) setAssets(nextAssets)
    } catch (error: unknown) {
      if (!isCancelled()) setAssetError(friendlyMessage(error, tRef.current('common.serviceError')))
    } finally {
      if (!isCancelled()) setAssetLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!selectedProjectId) {
      setChapters([])
      return
    }

    let cancelled = false
    void loadChapters(selectedProjectId, currentBranchId, () => cancelled)

    return () => {
      cancelled = true
    }
  }, [currentBranchId, loadChapters, selectedProjectId])

  useEffect(() => {
    if (!selectedProjectId) {
      setAssets([])
      return
    }

    let cancelled = false
    void loadAssets(selectedProjectId, () => cancelled)

    return () => {
      cancelled = true
    }
  }, [loadAssets, selectedProjectId])

  const selectedJobs = useMemo(
    () =>
      Object.values(jobs)
        .filter((job) => job.project_id === selectedProjectId)
        .sort(sortJobs),
    [jobs, selectedProjectId],
  )

  const scopedAssets = useMemo(() => assets.filter((asset) => asset.branch_id === currentBranchId), [assets, currentBranchId])

  useEffect(() => {
    setAssetColumnPages({
      screenplay: 0,
      storyboard: 0,
      panel_image: 0,
      audio: 0,
      video: 0,
    })
  }, [currentBranchId, selectedProjectId])

  const assetsByType = useMemo(() => {
    const grouped: Record<StationAssetType, Asset[]> = {
      screenplay: [],
      storyboard: [],
      panel_image: [],
      audio: [],
      video: [],
    }

    for (const asset of scopedAssets) {
      if ((stationAssetTypes as readonly AssetType[]).includes(asset.asset_type)) {
        grouped[asset.asset_type as StationAssetType].push(asset)
      }
    }

    for (const type of stationAssetTypes) {
      grouped[type] = grouped[type].sort(sortAssets)
    }

    return grouped
  }, [scopedAssets])

  const chaptersById = useMemo(() => new Map(chapters.map((chapter) => [chapter.id, chapter])), [chapters])
  const chapterCascadeStateById = useMemo(() => {
    const states = new Map<string, CascadeState>()
    for (const asset of scopedAssets) {
      if (asset.asset_type === 'chapter_text' && asset.chapter_id && asset.cascade_state) {
        states.set(asset.chapter_id, asset.cascade_state)
      }
    }
    return states
  }, [scopedAssets])
  const boardTextChapters = useMemo(
    () => filterBoardTextChapters(chapters, chapterCascadeStateById),
    [chapterCascadeStateById, chapters],
  )
  const runningJobs = useMemo(() => selectedJobs.filter((job) => isRunningJobStatus(job.status)), [selectedJobs])

  const refreshBoard = useCallback(async () => {
    if (!selectedProjectId) return
    await Promise.all([
      loadAssets(selectedProjectId),
      loadList(selectedProjectId, { branchId: currentBranchId }),
      loadChapters(selectedProjectId, currentBranchId),
      queryClient.invalidateQueries({ queryKey: ['pulse', 'summary'] }),
      queryClient.invalidateQueries({ queryKey: ['projects', user?.user_id, currentBranchId] }),
    ])
  }, [currentBranchId, loadAssets, loadChapters, loadList, queryClient, selectedProjectId, user?.user_id])

  const continueCascadeMutation = useMutation({
    mutationFn: chapterApi.continueCascade,
    onSuccess: async () => {
      toast.success(tRef.current('cascade.toast.continued'))
      await refreshBoard()
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, tRef.current('common.serviceError')))
    },
  })

  const stopCascadeMutation = useMutation({
    mutationFn: chapterApi.stopCascade,
    onSuccess: async () => {
      toast.success(tRef.current('cascade.toast.stopped'))
      await refreshBoard()
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, tRef.current('common.serviceError')))
    },
  })

  const cascadeLoadingChapterId = continueCascadeMutation.isPending
    ? continueCascadeMutation.variables
    : stopCascadeMutation.isPending
      ? stopCascadeMutation.variables
      : undefined

  const runningJobIds = useMemo(
    () => runningJobs.map((job) => job.id).sort(),
    [runningJobs],
  )
  const runningJobKey = runningJobIds.join('|')

  useEffect(() => {
    const previous = subscribedJobIdsRef.current
    const next = new Set(selectedProjectId && runningJobKey ? runningJobKey.split('|') : [])

    for (const jobId of previous) {
      if (!next.has(jobId)) unsubscribe(jobId)
    }
    for (const jobId of next) {
      if (!previous.has(jobId)) subscribe(jobId)
    }

    subscribedJobIdsRef.current = next
  }, [runningJobKey, selectedProjectId, subscribe, unsubscribe])

  useEffect(() => {
    if (!selectedProjectId) {
      observedRunningJobIdsRef.current = new Set()
      return
    }

    let shouldRefreshChapters = false
    for (const job of selectedJobs) {
      if (isRunningJobStatus(job.status)) {
        observedRunningJobIdsRef.current.add(job.id)
        continue
      }
      if (job.status === 'done' && job.result_chapter_id && observedRunningJobIdsRef.current.has(job.id)) {
        shouldRefreshChapters = true
        observedRunningJobIdsRef.current.delete(job.id)
        continue
      }
      if (job.status !== 'done') {
        observedRunningJobIdsRef.current.delete(job.id)
      }
    }

    if (shouldRefreshChapters) void loadChapters(selectedProjectId, currentBranchId)
  }, [currentBranchId, loadChapters, selectedJobs, selectedProjectId])

  const onProjectChange = (projectId: string) => {
    // 「全部项目」= 切到工厂总览(全项目聚合视图), 与作品库/一致性看板的「全部」语义一致;
    // 选具体项目则回到单作品模式。
    if (projectId === ALL_PROJECTS_VALUE) {
      setBoardView('overview')
      return
    }
    setBoardView('single')
    setDrawerSelection(null)
    setSubmitDrawerOpen(false)
    setSelectedProjectId(projectId)
    void select(projectId)
  }

  const drillIntoProject = (projectId: string) => {
    onProjectChange(projectId)
    setBoardView('single')
  }

  const openChapter = (chapter: Chapter) => {
    if (!selectedProjectId) return
    const ok = requestSelectChapter({ projectId: selectedProjectId, branchId: currentBranchId, chapterId: chapter.id }, () =>
      window.confirm(t('chapter.generate.switchAwayConfirm')),
    )
    if (ok) navigate(`/projects/${encodeURIComponent(selectedProjectId)}`)
  }

  const selectedProject = projects.find((project) => project.id === selectedProjectId) ?? null
  const loading = projectLoading || jobLoading || assetLoading || chapterLoading
  // 各 error 源 (projectStore/jobStore/assetError/chapterError) 均已在源头经 friendlyMessage 友好化, 直接展示即可。
  const error = projectError || jobError || assetError || chapterError

  return (
    <div className="mx-auto max-w-[1560px] p-8">
      <div className="flex w-full flex-col gap-6">
        <div className="flex w-full items-start justify-between gap-4">
          <div>
            <h2 className="m-0 text-3xl font-semibold tracking-normal">
              {t('board.title')}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('board.subtitle')}</p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Tabs value={boardView} onValueChange={(value) => setBoardView(value as BoardViewMode)}>
              <TabsList aria-label={t('board.view.label')}>
                <TabsTrigger value="single">{t('board.view.single')}</TabsTrigger>
                <TabsTrigger value="overview">{t('board.view.overview')}</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button disabled={!selectedProjectId} title={t('submitJob.description')} onClick={() => setSubmitDrawerOpen(true)}>
              {t('submitJob.open')}
            </Button>
            <ProjectCombobox
              projects={projects}
              value={boardView === 'overview' ? ALL_PROJECTS_VALUE : selectedProjectId}
              onChange={onProjectChange}
              isLoading={projectLoading}
              ariaLabel={t('board.selectProject')}
              includeAllProjects
            />
          </div>
        </div>

        {error ? <AlertBox type="error" message={error} /> : null}

        {boardView === 'single' ? (
          <>
            {/* deepseek Phase2: 移除冗余摘要行 — 工厂总览已是独立全页视图([工厂总览] tab),
                单作品模式只留工位列, 全局态势走总览视图, 避免双重维护。 */}
            <LoadingOverlay loading={loading}>
              {!projects.length && !projectLoading ? (
                <EmptyState description={t('board.emptyProjects')} className="mt-20" />
              ) : (
                <div className="space-y-6">
                  {boardStages.map((stage) => (
                    <BoardStage
                      key={stage.marker}
                      marker={stage.marker}
                      label={t(stage.labelKey)}
                      hint={t(stage.hintKey)}
                    >
                      <div className={cn('grid items-start gap-4', stage.grid)}>
                        {stage.columns.map((columnKey) => {
                          const column = boardColumns.find((item) => item.key === columnKey)!
                          if (column.key === 'chapter_text') {
                            return (
                              <BoardColumn
                                key={column.key}
                                className="w-full"
                                title={t(column.label)}
                                count={boardTextChapters.length}
                                aside={runningJobs.length ? <Badge variant="outline">{t('board.runningCount', { count: formatCount(runningJobs.length) })}</Badge> : null}
                              >
                            {runningJobs.length ? (
                              <div className="flex w-full flex-col gap-3">
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                  <Cog className="h-3.5 w-3.5" />
                                  <span>{t('board.sectionGenerating')}</span>
                                  <span className="nf-mono">({formatCount(runningJobs.length)})</span>
                                </div>
                                {runningJobs.map((job) => (
                                  <JobCard key={job.id} job={job} onOpen={() => setDrawerSelection({ kind: 'job', job })} />
                                ))}
                              </div>
                            ) : null}
                            {runningJobs.length ? <Separator /> : null}
                            <div className="flex w-full flex-col gap-3">
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <FileText className="h-3.5 w-3.5" />
                                <span>{t('board.sectionInFlight')}</span>
                                <span className="nf-mono">({formatCount(boardTextChapters.length)})</span>
                              </div>
                              {boardTextChapters.length ? (
                                boardTextChapters.map((chapter) => (
                                  <ChapterTextCard
                                    key={chapter.id}
                                    chapter={chapter}
                                    cascadeState={chapterCascadeStateById.get(chapter.id)}
                                    cascadeLoadingChapterId={cascadeLoadingChapterId}
                                    onCascadeContinue={(chapterId) => continueCascadeMutation.mutate(chapterId)}
                                    onCascadeStop={(chapterId) => stopCascadeMutation.mutate(chapterId)}
                                    onOpen={() => openChapter(chapter)}
                                  />
                                ))
                              ) : (
                                <EmptyState description={t('board.emptyChapterTexts')} />
                              )}
                            </div>
                          </BoardColumn>
                        )
                      }

                      const columnAssets = assetsByType[column.key]
                      const pagedAssets = pageItems(columnAssets, assetColumnPages[column.key], PAGE_SIZE)
                          return (
                            <BoardColumn key={column.key} className="w-full" title={t(column.label)} count={columnAssets.length}>
                              {columnAssets.length ? (
                            <>
                              {pagedAssets.pageItems.map((asset) => (
                                <AssetCard
                                  key={asset.id}
                                  asset={asset}
                                  chaptersById={chaptersById}
                                  cascadeLoadingChapterId={cascadeLoadingChapterId}
                                  onCascadeContinue={(chapterId) => continueCascadeMutation.mutate(chapterId)}
                                  onCascadeStop={(chapterId) => stopCascadeMutation.mutate(chapterId)}
                                  onOpen={() => setDrawerSelection({ kind: 'asset', asset })}
                                />
                              ))}
                              <StructuredAssetPagination
                                page={pagedAssets.page}
                                pageCount={pagedAssets.pageCount}
                                total={columnAssets.length}
                                totalLabelKey="pagination.totalAssets"
                                onPageChange={(page) => setAssetColumnPages((current) => ({ ...current, [column.key]: page }))}
                              />
                            </>
                          ) : (
                                <EmptyState description={t('board.emptyAssets')} />
                              )}
                            </BoardColumn>
                          )
                        })}
                      </div>
                    </BoardStage>
                  ))}
                </div>
              )}
            </LoadingOverlay>
          </>
        ) : (
          <FactoryOverviewMatrix
            projects={summaryQuery.data?.projects ?? []}
            loading={summaryQuery.isLoading || summaryQuery.isFetching}
            error={summaryQuery.error ? friendlyMessage(summaryQuery.error, tRef.current('common.serviceError')) : null}
            onSelectProject={drillIntoProject}
          />
        )}
      </div>

      <AssetDetailDrawer selection={drawerSelection} assets={scopedAssets} chaptersById={chaptersById} onClose={() => setDrawerSelection(null)} />
      <SubmitJobDrawer
        open={submitDrawerOpen}
        project={selectedProject}
        branchId={currentBranchId}
        jobs={selectedJobs}
        onClose={() => setSubmitDrawerOpen(false)}
        onSubmitted={(projectId) =>
          Promise.all([
            loadList(projectId, { branchId: currentBranchId }),
            loadChapters(projectId, currentBranchId),
            queryClient.invalidateQueries({ queryKey: ['pulse', 'summary'] }),
          ]).then(() => undefined)
        }
      />
    </div>
  )
}
